import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Button } from '../common/Catalyst/button';
import {
  AmenityReturn,
  ReservationChecklistItem,
  ReservationDocumentReturn,
  ReservationDocumentsReturn,
  ReservationFeeLine,
  ReservationInspectionKind,
  ReservationInspectionReturn,
  ReservationReturn,
} from '../clients/rust-api-client';
import {
  fileService,
  reservationOperationsService,
  reservationService,
} from '../clients/ApiClients';
import { formatMoney, savePdfResponse } from '../utils/billingHelpers';
import AttachmentThumb from '../violation/comments/AttachmentThumb';
import {
  formatDayLabel,
  formatLocalRange,
  formatLocalTime,
  formatMoment,
  localDay,
  reservationErrorMessage,
} from './reservationMeta';
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_STATUS_LABELS,
  FEE_LINE_KIND_LABELS,
  INSPECTION_KIND_LABELS,
  INSPECTION_OUTCOME_LABELS,
  SETTLE_KIND_TEXT,
  canSettleDeposit,
  checkInState,
  checklistFrom,
  depositReturned,
  depositStatusLabel,
  nowLocalIn,
  outcomeOf,
  returnedText,
  settlePlan,
  showFeeBreakdown,
} from './phaseThreeMeta';
import { checkboxClass, helpClass, inputClass, labelClass } from './formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * Phase 3 of a booking's detail page (#642): its deposit and settlement,
 * access code and check-in, inspections, and required documents. Each
 * section hides itself when the booking has nothing of that kind, so a
 * plain tennis booking reads as it did before.
 *
 * Board members see every section read-only: no settle, no check-in, no
 * review, no uploads.
 */
interface SectionProps {
  reservation: ReservationReturn;
  canManage: boolean;
  /** The booking changed (or may have): reload it and its history. */
  onChanged: (updated?: ReservationReturn) => void;
}

const sectionTitle = 'mb-2 text-[15px] font-semibold text-fg';
const box = 'rounded-lg border border-line px-4 py-3 text-[13px]';

const nowSeconds = () => Math.floor(Date.now() / 1000);

// ---------------------------------------------------------------------------
// Paid money given back (#703)
// ---------------------------------------------------------------------------

/** What became of a paid fee or deposit that was owed back. Nothing for any other status. */
export const ReturnedNote: React.FC<{ what: 'fee' | 'deposit'; status: string; amount: string }> = ({
  what,
  status,
  amount,
}) => {
  const text = returnedText(what, status, amount);
  if (!text) return null;
  return (
    <p
      className={clsx(
        'rounded-md px-3 py-2 text-[13px] text-fg-secondary',
        status === 'refund_due' ? 'bg-warning/10' : 'bg-fg/5'
      )}
    >
      {text}
    </p>
  );
};

/**
 * A booking from before #703 whose paid fee or deposit stopped at
 * `refund_due`: return it through the same entries every cancellation
 * now posts. Asks first, because the old workaround was a hand-posted
 * credit and refund, and returning it again would pay twice.
 */
export const ReturnMoneyAction: React.FC<SectionProps> = ({ reservation: r, canManage, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!canManage || (r.fee_status !== 'refund_due' && r.deposit_status !== 'refund_due')) return null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await reservationOperationsService.returnReservationMoney({
        reservationId: r.id,
        reservationReturnMoney: { confirm_not_already_returned: true },
      });
      setOpen(false);
      onChanged(res.data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't return the money."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        outline
        onClick={() => {
          setChecked(false);
          setError(null);
          setOpen(true);
        }}
      >
        Return to account…
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Return the paid money</DialogTitle>
        <DialogDescription>
          {r.amenity_name} · {r.home_address}
        </DialogDescription>
        <DialogBody className="space-y-3 text-[13px] text-fg-secondary">
          <p>
            This credits the home’s account with what is owed back and, unless the association allows credit
            balances, records it as refunded by cheque.
          </p>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>
              I checked the{' '}
              <Link to={`/hoa/${r.hoa_id}/home/${r.home_id}/billing`} className="text-accent-fg hover:underline">
                home’s ledger
              </Link>{' '}
              and nobody has already credited or refunded this by hand.
            </span>
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" onClick={submit} disabled={!checked || saving}>
            {saving ? 'Returning…' : 'Return to account'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Fee breakdown — for the properties panel and previews
// ---------------------------------------------------------------------------

/** "Base fee $150.00 · Weekend rate $200.00 · Guests $20.00", one line each. */
export const FeeBreakdown: React.FC<{ lines: ReservationFeeLine[] | null | undefined; className?: string }> = ({
  lines,
  className,
}) => {
  if (!showFeeBreakdown(lines)) return null;
  return (
    <ul className={clsx('space-y-0.5 text-[12px] text-fg-muted', className)} aria-label="How the fee is made up">
      {lines!.map((l, i) => (
        <li key={`${l.kind}-${i}`} className="flex justify-between gap-3">
          <span>{l.label || FEE_LINE_KIND_LABELS[l.kind]}</span>
          <span className="tabular-nums">{formatMoney(l.amount)}</span>
        </li>
      ))}
    </ul>
  );
};

// ---------------------------------------------------------------------------
// Deposit
// ---------------------------------------------------------------------------

export const DepositSection: React.FC<SectionProps> = ({ reservation: r, canManage, onChanged }) => {
  const [settling, setSettling] = useState(false);
  if (r.deposit_status === 'none') return null;
  const settleable = canManage && canSettleDeposit(r, nowSeconds());
  const kept = r.deposit_deduction_amount;
  return (
    <section aria-labelledby="deposit-title">
      <h2 id="deposit-title" className={sectionTitle}>
        Deposit
      </h2>
      <div className={clsx(box, 'space-y-2')}>
        <p className="text-fg-secondary">
          <span className="font-medium text-fg">{formatMoney(r.deposit_amount)}</span> ·{' '}
          {depositStatusLabel(r.deposit_status)}
          {r.deposit_settled_at && <span className="text-fg-muted"> · settled {formatMoment(r.deposit_settled_at)}</span>}
        </p>
        {kept && r.deposit_status !== 'held' && (
          <p className="text-fg-secondary">
            Kept {formatMoney(kept)}
            {r.deposit_deduction_reason && <> — “{r.deposit_deduction_reason}”</>}
          </p>
        )}
        {r.deposit_status === 'held' && !settleable && (
          <p className={helpClass}>
            Held as a liability until the booking is over. It is returned automatically after the
            association’s release period unless an inspection after use records issues.
          </p>
        )}
        <ReturnedNote what="deposit" status={r.deposit_status} amount={depositReturned(r)} />
        {settleable && (
          <Button outline onClick={() => setSettling(true)}>
            Settle deposit…
          </Button>
        )}
      </div>
      {settleable && (
        <SettleDepositDialog
          open={settling}
          reservation={r}
          onClose={() => setSettling(false)}
          onSettled={(u) => {
            setSettling(false);
            onChanged(u);
          }}
        />
      )}
    </section>
  );
};

const SettleDepositDialog: React.FC<{
  open: boolean;
  reservation: ReservationReturn;
  onClose: () => void;
  onSettled: (r: ReservationReturn) => void;
}> = ({ open, reservation: r, onClose, onSettled }) => {
  const [release, setRelease] = useState(r.deposit_amount);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRelease(r.deposit_amount);
      setReason('');
      setError(null);
    }
  }, [open, r.deposit_amount]);

  const plan = settlePlan(r.deposit_amount, release, reason);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan.valid) return;
    setSaving(true);
    setError(null);
    try {
      const res = await reservationOperationsService.settleReservationDeposit({
        reservationId: r.id,
        reservationDepositSettle: {
          release_amount: plan.release,
          deduction_reason: plan.needsReason ? reason.trim() : null,
        },
      });
      onSettled(res.data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't settle the deposit."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Settle the deposit</DialogTitle>
        <DialogDescription>
          {r.amenity_name} · {r.home_address} · {formatMoney(r.deposit_amount)} held
        </DialogDescription>
        <DialogBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <label className={labelClass}>
              Return to the household
              <span className={clsx(inputClass, 'flex w-40 items-center', plan.problem && 'border-danger')}>
                <span className="text-fg-muted">$</span>
                <input
                  inputMode="decimal"
                  value={release}
                  onChange={(e) => setRelease(e.target.value.replace(/[^\d.]/g, ''))}
                  className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
                  aria-label="Amount to return"
                />
              </span>
            </label>
            <Button plain type="button" onClick={() => setRelease(r.deposit_amount)}>
              All
            </Button>
            <Button plain type="button" onClick={() => setRelease('0.00')}>
              None
            </Button>
          </div>
          {plan.problem && <p className="text-[12px] text-danger">{plan.problem}</p>}
          {plan.kind && (
            <p className="text-[13px] text-fg-secondary">
              {SETTLE_KIND_TEXT[plan.kind]}
              {plan.kind === 'keep_some' && ` Keeping ${formatMoney(plan.kept)}, returning ${formatMoney(plan.release)}.`}
            </p>
          )}
          {plan.needsReason && (
            <label className={labelClass}>
              Why some is kept — the household is shown this
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Carpet cleaning after a spill — invoice attached to the inspection."
                className={clsx(inputClass, 'resize-y')}
              />
            </label>
          )}
          <p className={helpClass}>
            An unpaid deposit is simply reversed. A paid one is returned to the home’s account — refunded by
            cheque, or left as a credit if the association allows credit balances — and what is kept moves
            from deposits held to amenity income.
          </p>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!plan.valid || saving}>
            {saving ? 'Settling…' : 'Settle deposit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Access code and check-in
// ---------------------------------------------------------------------------

export const AccessSection: React.FC<SectionProps & { amenity: AmenityReturn | null }> = ({
  reservation: r,
  amenity,
  canManage,
  onChanged,
}) => {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(r.access_code ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setCode(r.access_code ?? ''), [r.access_code]);

  const mode = amenity?.access_code_mode ?? (r.access_code ? 'per_booking' : 'none');
  const live = r.status === 'approved' || r.status === 'completed' || r.status === 'no_show';
  const hasAccess = mode !== 'none' || !!r.access_code || !!r.access_instructions;
  if (!live && !r.checked_in_at) return null;

  const run = async (label: string, call: () => Promise<{ data: ReservationReturn }>) => {
    setBusy(label);
    setError(null);
    try {
      onChanged((await call()).data);
    } catch (err) {
      setError(reservationErrorMessage(err, `Couldn't ${label}.`));
    } finally {
      setBusy(null);
    }
  };

  const ended = r.ends_at <= nowSeconds();
  const thisYear = new Date().getFullYear();
  // The server reports the window it will judge a check-in by; with none
  // reported, offer the button and let a CHECK_IN_CLOSED refusal explain.
  const checkIn = checkInState(r, nowLocalIn(r.timezone));
  const mayCheckIn = canManage && r.status === 'approved' && !r.checked_in_at && !ended;

  return (
    <section aria-labelledby="access-title">
      <h2 id="access-title" className={sectionTitle}>
        Access and check-in
      </h2>
      <div className={clsx(box, 'space-y-3')}>
        {hasAccess && (
          <div className="space-y-1">
            {editing ? (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  run('save the code', () =>
                    reservationService.patchReservation({
                      reservationId: r.id,
                      reservationPatch: { access_code: code.trim() || null },
                    })
                  ).then(() => setEditing(false));
                }}
              >
                <label className={labelClass}>
                  Access code
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={clsx(inputClass, 'w-40 font-mono')}
                    maxLength={32}
                  />
                </label>
                <Button color="sky" type="submit" disabled={busy !== null}>
                  Save
                </Button>
                <Button plain onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </form>
            ) : (
              <p className="text-fg-secondary">
                Access code{' '}
                {r.access_code ? (
                  <span className="rounded bg-fg/5 px-1.5 py-0.5 font-mono text-fg">{r.access_code}</span>
                ) : (
                  <span className="text-fg-muted">{mode === 'per_booking' ? 'issued on approval' : 'none'}</span>
                )}
                {canManage && live && !ended && (
                  <button
                    type="button"
                    className="ml-2 text-[12px] text-accent-fg hover:underline"
                    onClick={() => setEditing(true)}
                  >
                    {r.access_code ? 'Change' : 'Set'}
                  </button>
                )}
              </p>
            )}
            {r.access_visible_from_local && (
              <p className={helpClass}>
                The household sees it from {formatDayLabel(localDay(r.access_visible_from_local), thisYear)},{' '}
                {formatLocalTime(r.access_visible_from_local)} until the booking ends.
              </p>
            )}
            {r.access_instructions && (
              <p className="whitespace-pre-line text-fg-secondary">{r.access_instructions}</p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-fg-secondary">
            {r.checked_in_at ? (
              <>
                <CheckCircleIcon className="mr-1 inline h-4 w-4 text-success" aria-hidden />
                Checked in {formatMoment(r.checked_in_at)}
                {r.checked_out_at && <> · checked out {formatMoment(r.checked_out_at)}</>}
              </>
            ) : (
              <span className="text-fg-muted">Not checked in.</span>
            )}
          </span>
          {mayCheckIn && checkIn === 'not_yet' && r.check_in_opens_at_local && (
            <span className={helpClass}>
              Check-in opens {formatDayLabel(localDay(r.check_in_opens_at_local), thisYear)},{' '}
              {formatLocalTime(r.check_in_opens_at_local)}.
            </span>
          )}
          {mayCheckIn && checkIn === 'closed' && <span className={helpClass}>Check-in has closed.</span>}
          {mayCheckIn && (checkIn === 'open' || checkIn === 'unknown') && (
            <Button
              outline
              disabled={busy !== null}
              onClick={() =>
                run('check the household in', () =>
                  reservationOperationsService.checkInReservation({ reservationId: r.id })
                )
              }
            >
              {busy === 'check the household in' ? 'Checking in…' : 'Check in'}
            </Button>
          )}
          {canManage && r.checked_in_at && !r.checked_out_at && (
            <Button
              outline
              disabled={busy !== null}
              onClick={() =>
                run('check the household out', () =>
                  reservationOperationsService.checkOutReservation({ reservationId: r.id })
                )
              }
            >
              {busy === 'check the household out' ? 'Checking out…' : 'Check out'}
            </Button>
          )}
        </div>
        <ErrorAlert>{error}</ErrorAlert>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

export const InspectionsSection: React.FC<SectionProps & { amenity: AmenityReturn | null }> = ({
  reservation: r,
  amenity,
  canManage,
  onChanged,
}) => {
  const [inspections, setInspections] = useState<ReservationInspectionReturn[]>([]);
  const [recording, setRecording] = useState<ReservationInspectionKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await reservationOperationsService.listReservationInspections({ reservationId: r.id });
      setInspections(res.data.inspections);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the inspections."));
    }
  }, [r.id]);

  useEffect(() => {
    load();
  }, [load]);

  const checklist = amenity?.inspection_checklist ?? [];
  const relevant = r.deposit_status !== 'none' || checklist.length > 0 || inspections.length > 0;
  if (!relevant) return null;
  const started = r.starts_at <= nowSeconds();
  const open = ['approved', 'completed', 'no_show'].includes(r.status);

  return (
    <section aria-labelledby="inspections-title">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 id="inspections-title" className="mr-auto text-[15px] font-semibold text-fg">
          Inspections
        </h2>
        {canManage && open && (
          <>
            {!started && (
              <Button plain onClick={() => setRecording('pre')}>
                Record before use
              </Button>
            )}
            {started && (
              <Button plain onClick={() => setRecording('post')}>
                Record after use
              </Button>
            )}
          </>
        )}
      </div>
      <ErrorAlert>{error}</ErrorAlert>
      {inspections.length === 0 ? (
        <p className="text-[13px] text-fg-muted">
          None recorded.
          {r.deposit_status === 'held' &&
            ' An inspection after use that finds issues stops the deposit being returned automatically.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {inspections.map((i) => (
            <InspectionCard key={i.id} inspection={i} reservationId={r.id} canManage={canManage} onChanged={load} />
          ))}
        </ul>
      )}
      {canManage && (
        <RecordInspectionDialog
          kind={recording}
          reservation={r}
          checklist={checklist}
          onClose={() => setRecording(null)}
          onRecorded={() => {
            setRecording(null);
            load();
            onChanged();
          }}
        />
      )}
    </section>
  );
};

const InspectionCard: React.FC<{
  inspection: ReservationInspectionReturn;
  reservationId: string;
  canManage: boolean;
  onChanged: () => void;
}> = ({ inspection: i, reservationId, canManage, onChanged }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await reservationOperationsService.uploadReservationInspectionPhoto({
          reservationId,
          inspectionId: i.id,
          file,
        });
      }
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't upload the photo."));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <li className={clsx(box, 'space-y-2')}>
      <p className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-fg">{INSPECTION_KIND_LABELS[i.kind]}</span>
        <span
          className={clsx(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px]',
            i.outcome === 'issues' ? 'bg-warning/15 text-fg' : 'bg-success/10 text-fg-secondary'
          )}
        >
          {i.outcome === 'issues' ? (
            <ExclamationTriangleIcon className="h-3.5 w-3.5 text-warning" aria-hidden />
          ) : (
            <CheckCircleIcon className="h-3.5 w-3.5 text-success" aria-hidden />
          )}
          {INSPECTION_OUTCOME_LABELS[i.outcome]}
        </span>
        <span className="text-[12px] text-fg-muted">{formatMoment(i.inspected_at)}</span>
      </p>
      {i.checklist.length > 0 && (
        <ul className="space-y-0.5">
          {i.checklist.map((c, idx) => (
            <li key={idx} className={clsx('text-[13px]', c.ok ? 'text-fg-secondary' : 'text-fg')}>
              {c.ok ? '✓' : '✗'} {c.item}
              {c.note && <span className="text-fg-muted"> — {c.note}</span>}
            </li>
          ))}
        </ul>
      )}
      {i.notes && <p className="whitespace-pre-line text-fg-secondary">{i.notes}</p>}
      {i.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {i.photos.map((p) => (
            <AttachmentThumb key={p.id} attachment={p} />
          ))}
        </div>
      )}
      {canManage && (
        <div>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="sr-only"
            id={`inspection-photo-${i.id}`}
            onChange={(e) => upload(e.target.files)}
          />
          <button
            type="button"
            className="text-[12px] text-accent-fg hover:underline disabled:opacity-50"
            onClick={() => input.current?.click()}
            disabled={busy}
          >
            {busy ? 'Uploading…' : 'Add photos'}
          </button>
        </div>
      )}
      <ErrorAlert>{error}</ErrorAlert>
    </li>
  );
};

const RecordInspectionDialog: React.FC<{
  kind: ReservationInspectionKind | null;
  reservation: ReservationReturn;
  checklist: string[];
  onClose: () => void;
  onRecorded: () => void;
}> = ({ kind, reservation: r, checklist, onClose, onRecorded }) => {
  const [items, setItems] = useState<ReservationChecklistItem[]>([]);
  const [notes, setNotes] = useState('');
  const [issues, setIssues] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kind) {
      setItems(checklistFrom(checklist));
      setNotes('');
      setIssues(false);
      setError(null);
    }
  }, [kind, checklist]);

  // With a checklist, the outcome follows the ticks; without one, the manager says.
  const outcome = items.length > 0 ? outcomeOf(items) : issues ? 'issues' : 'ok';
  const setItem = (idx: number, patch: Partial<ReservationChecklistItem>) =>
    setItems((list) => list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind) return;
    setSaving(true);
    setError(null);
    try {
      await reservationOperationsService.createReservationInspection({
        reservationId: r.id,
        reservationInspectionCreate: {
          kind,
          checklist: items.map((i) => ({ ...i, note: i.note?.trim() || null })),
          notes: notes.trim() || null,
          outcome,
        },
      });
      onRecorded();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't record the inspection."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={kind !== null} onClose={onClose} size="xl">
      <form onSubmit={submit}>
        <DialogTitle>{kind ? `Inspection — ${INSPECTION_KIND_LABELS[kind].toLowerCase()}` : 'Inspection'}</DialogTitle>
        <DialogDescription>
          {r.amenity_name} · {formatLocalRange(r.start_local, r.end_local)}
        </DialogDescription>
        <DialogBody className="space-y-4">
          {items.length > 0 ? (
            <ul className="space-y-2">
              {items.map((it, idx) => (
                <li key={idx} className="space-y-1">
                  <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={it.ok}
                      onChange={(e) => setItem(idx, { ok: e.target.checked })}
                    />
                    {it.item}
                  </label>
                  {!it.ok && (
                    <input
                      value={it.note ?? ''}
                      onChange={(e) => setItem(idx, { note: e.target.value })}
                      placeholder="What is wrong?"
                      className={clsx(inputClass, 'ml-6 w-[calc(100%-1.5rem)]')}
                      aria-label={`Note for ${it.item}`}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={issues}
                onChange={(e) => setIssues(e.target.checked)}
              />
              Issues found
            </label>
          )}
          <label className={labelClass}>
            Notes
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          <p className={helpClass}>
            Outcome: {INSPECTION_OUTCOME_LABELS[outcome]}. Add photos to the inspection once it is recorded.
            {outcome === 'issues' && kind === 'post' && r.deposit_status === 'held' &&
              ' Issues after use keep the deposit held until you settle it.'}
          </p>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Record inspection'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Required documents
// ---------------------------------------------------------------------------

export const DocumentsSection: React.FC<SectionProps> = ({ reservation: r, canManage, onChanged }) => {
  const [docs, setDocs] = useState<ReservationDocumentsReturn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<{ doc: ReservationDocumentReturn; decision: 'accepted' | 'rejected' } | null>(
    null
  );

  const load = useCallback(async () => {
    try {
      setDocs((await reservationOperationsService.listReservationDocuments({ reservationId: r.id })).data);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the documents."));
    }
  }, [r.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!docs || docs.requirements.length === 0) return null;
  const thisYear = new Date().getFullYear();
  const live = r.status === 'pending' || r.status === 'approved';

  return (
    <section aria-labelledby="documents-title">
      <h2 id="documents-title" className={sectionTitle}>
        Required documents
      </h2>
      <ErrorAlert>{error}</ErrorAlert>
      <ul className="space-y-3">
        {docs.requirements.map((req) => {
          const theirs = docs.documents.filter((d) => d.requirement_id === req.requirement.id);
          return (
            <li key={req.requirement.id} className={clsx(box, 'space-y-2')}>
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-fg">{req.requirement.title}</span>
                <span className="text-[12px] text-fg-muted">{DOCUMENT_KIND_LABELS[req.requirement.kind]}</span>
                <span
                  className={clsx(
                    'ml-auto rounded-full px-2 py-0.5 text-[12px]',
                    req.satisfied ? 'bg-success/10 text-fg-secondary' : 'bg-warning/15 text-fg'
                  )}
                >
                  {req.satisfied ? 'Satisfied' : `Due ${formatDayLabel(req.due_on, thisYear)}`}
                </span>
              </p>
              {req.requirement.description && <p className={helpClass}>{req.requirement.description}</p>}
              {theirs.length === 0 ? (
                <p className="text-fg-muted">Nothing uploaded yet.</p>
              ) : (
                <ul className="divide-y divide-line/60">
                  {theirs.map((d) => (
                    <DocumentRow
                      key={d.id}
                      doc={d}
                      canReview={canManage && d.status === 'submitted'}
                      onReview={(decision) => setReviewing({ doc: d, decision })}
                    />
                  ))}
                </ul>
              )}
              {canManage && live && (
                <UploadDocument
                  reservationId={r.id}
                  requirementId={req.requirement.id}
                  requiresExpiry={req.requirement.requires_expiry}
                  onUploaded={() => {
                    load();
                    onChanged();
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
      {canManage && (
        <ReviewDocumentDialog
          reservationId={r.id}
          review={reviewing}
          onClose={() => setReviewing(null)}
          onReviewed={() => {
            setReviewing(null);
            load();
            onChanged();
          }}
        />
      )}
    </section>
  );
};

const DocumentRow: React.FC<{
  doc: ReservationDocumentReturn;
  canReview: boolean;
  onReview: (decision: 'accepted' | 'rejected') => void;
}> = ({ doc: d, canReview, onReview }) => {
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setError(null);
    try {
      const res = await fileService.downloadFile({ id: d.file_id }, { responseType: 'blob' as const });
      savePdfResponse(res, d.file_name);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't download the file."));
    }
  };
  const thisYear = new Date().getFullYear();
  return (
    <li className="space-y-1 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={download} className="text-accent-fg hover:underline">
          {d.file_name}
        </button>
        <span className="text-[12px] text-fg-muted">
          {DOCUMENT_STATUS_LABELS[d.status]}
          {d.expires_on && ` · expires ${formatDayLabel(d.expires_on, thisYear)}`}
          {d.submitted_by === 'user' ? ' · uploaded by management' : ' · uploaded by the household'}
        </span>
        {canReview && (
          <span className="ml-auto flex gap-1">
            <Button plain onClick={() => onReview('accepted')}>
              Accept
            </Button>
            <Button plain onClick={() => onReview('rejected')}>
              Reject
            </Button>
          </span>
        )}
      </div>
      {d.review_note && <p className="text-[12px] text-fg-secondary">“{d.review_note}”</p>}
      <ErrorAlert>{error}</ErrorAlert>
    </li>
  );
};

const UploadDocument: React.FC<{
  reservationId: string;
  requirementId: string;
  requiresExpiry: boolean;
  onUploaded: () => void;
}> = ({ reservationId, requirementId, requiresExpiry, onUploaded }) => {
  const input = useRef<HTMLInputElement>(null);
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await reservationOperationsService.uploadReservationDocument({
        reservationId,
        requirementId,
        file,
        expiresOn: expires || undefined,
      });
      setExpires('');
      onUploaded();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't upload the document."));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-line/60 pt-2">
      {requiresExpiry && (
        <label className={labelClass}>
          Expires on
          <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={inputClass} />
        </label>
      )}
      <input
        ref={input}
        type="file"
        className="sr-only"
        id={`doc-${reservationId}-${requirementId}`}
        onChange={(e) => upload(e.target.files)}
      />
      <Button plain onClick={() => input.current?.click()} disabled={busy || (requiresExpiry && !expires)}>
        {busy ? 'Uploading…' : 'Upload on the household’s behalf'}
      </Button>
      {requiresExpiry && !expires && <span className={helpClass}>Enter the expiry date first.</span>}
      <ErrorAlert className="w-full">{error}</ErrorAlert>
    </div>
  );
};

const ReviewDocumentDialog: React.FC<{
  reservationId: string;
  review: { doc: ReservationDocumentReturn; decision: 'accepted' | 'rejected' } | null;
  onClose: () => void;
  onReviewed: () => void;
}> = ({ reservationId, review, onClose, onReviewed }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNote('');
    setError(null);
  }, [review]);

  const rejecting = review?.decision === 'rejected';
  const valid = !rejecting || note.trim().length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!review || !valid) return;
    setSaving(true);
    setError(null);
    try {
      await reservationOperationsService.reviewReservationDocument({
        reservationId,
        documentId: review.doc.id,
        reservationDocumentReview: { decision: review.decision, note: note.trim() || null },
      });
      onReviewed();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the review."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={review !== null} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>{rejecting ? 'Reject the document' : 'Accept the document'}</DialogTitle>
        <DialogDescription>
          {review?.doc.requirement_title} · {review?.doc.file_name}
        </DialogDescription>
        <DialogBody className="space-y-3">
          <label className={labelClass}>
            {rejecting ? 'What is wrong — the household is shown this' : 'Note (optional)'}
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={rejecting ? 'e.g. The certificate must name the association as additional insured.' : ''}
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color={rejecting ? 'red' : 'sky'} type="submit" disabled={!valid || saving}>
            {saving ? 'Saving…' : rejecting ? 'Reject' : 'Accept'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
